/**
 * The column-role tag vocabulary: `<prefix><measureId>` on a grid question
 * states which measure fills an analytical role (pain importance and
 * satisfaction, equity relevance and distinctiveness).
 *
 * One owner, Python's `models/questionnaire/opportunity.py`
 * (`OPPORTUNITY_TAG_BY_ROLE`): the assembler stamps the tags from PLAN's typed
 * `matrixIntent.columnOpportunityRoles`, and the recipe manifest
 * (`config/questionnaire-recipe-registry.json`, `opportunityRoleTags`) carries
 * the same map here. The report projection finds a declared map axis's column
 * by it, never by the column's label.
 *
 * Cache scope: one-shot, not cached.
 */

import recipeManifest from "../../../../config/questionnaire-recipe-registry.json";

export type OpportunityRole = keyof typeof recipeManifest.opportunityRoleTags;

export const OPPORTUNITY_TAG_BY_ROLE: Readonly<Record<OpportunityRole, string>> =
  recipeManifest.opportunityRoleTags;

/** The measure ids among `measureIds` whose tag on the question states `role`. */
export function measureIdsFillingRole(
  tags: ReadonlyArray<string>,
  measureIds: ReadonlyArray<string>,
  role: OpportunityRole,
): string[] {
  const prefix = OPPORTUNITY_TAG_BY_ROLE[role];
  const tagged = new Set(tags);
  return measureIds.filter((measureId) => tagged.has(`${prefix}${measureId}`));
}

/** The role a measure's stamped tag states, or null when it fills none. */
export function opportunityRoleOfMeasure(
  tags: ReadonlyArray<string>,
  measureId: string,
): OpportunityRole | null {
  const roles = (Object.keys(OPPORTUNITY_TAG_BY_ROLE) as OpportunityRole[]).filter(
    (role) => tags.includes(`${OPPORTUNITY_TAG_BY_ROLE[role]}${measureId}`),
  );
  return roles.length === 1 ? roles[0] : null;
}
